import React, { useEffect, useState, lazy, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Palette, RotateCcw, Check, Eye, Sparkles, Type, Square, Circle, RectangleHorizontal, ImageIcon, Upload, Trash2, X as XIcon, Save } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useTheme, ThemeConfig } from '../../context/ThemeContext';
function ColorPicker({
  label,
  value,
  onChange
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return <div className="flex items-center justify-between gap-4" data-id="element-822">
      <label className="text-sm text-slate-300 font-medium" data-id="element-823">{label}</label>
      <div className="flex items-center gap-2" data-id="element-824">
        <div className="relative" data-id="element-825">
          <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-8 h-8 rounded-lg border border-slate-700 cursor-pointer bg-transparent appearance-none [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-lg [&::-webkit-color-swatch]:border-none" data-id="element-826" />
        </div>
        <input type="text" value={value} onChange={e => onChange(e.target.value)} className="w-24 bg-slate-950 border border-slate-800 rounded-lg px-2 py-1 text-xs text-white font-mono focus:border-fox-500 focus:outline-none" data-id="element-827" />
      </div>
    </div>;
}
export function ThemeEditor() {
  const {
    currentTheme,
    setTheme,
    prebuiltThemes,
    resetTheme,
    uploadedMedia,
    addMedia,
    removeMedia,
    saveTheme,
    savedCustomThemes,
    saveCustomTheme,
    deleteCustomTheme,
    hasUnsavedChanges
  } = useTheme();
  const [activeSection, setActiveSection] = useState<'prebuilt' | 'colors' | 'typography' | 'components' | 'images'>('prebuilt');
  const [customTheme, setCustomTheme] = useState<ThemeConfig>({
    ...currentTheme
  });
  const [showPreview, setShowPreview] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [customThemeName, setCustomThemeName] = useState('');
  // Sync customTheme with currentTheme when currentTheme changes externally (e.g. on load)
  useEffect(() => {
    setCustomTheme({
      ...currentTheme
    });
  }, [currentTheme]);
  const updateCustomColor = (key: keyof ThemeConfig['colors'], value: string) => {
    const updated = {
      ...customTheme,
      colors: {
        ...customTheme.colors,
        [key]: value
      }
    };
    setCustomTheme(updated);
    setTheme(updated);
  };
  const updateCustomFont = (key: keyof ThemeConfig['fonts'], value: string) => {
    const updated = {
      ...customTheme,
      fonts: {
        ...customTheme.fonts,
        [key]: value
      }
    };
    setCustomTheme(updated);
    setTheme(updated);
  };
  const applyPrebuilt = (theme: ThemeConfig) => {
    setCustomTheme({
      ...theme
    });
    setTheme(theme);
  };
  const handleReset = () => {
    resetTheme();
    setCustomTheme({
      ...prebuiltThemes[0]
    });
  };
  const updateImage = (key: keyof ThemeConfig['images'], value: string) => {
    const updated = {
      ...customTheme,
      images: {
        ...customTheme.images,
        [key]: value
      }
    };
    setCustomTheme(updated);
    setTheme(updated);
  };
  const handleFileUpload = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach(file => {
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = e => {
        const url = e.target?.result as string;
        const sizeKB = Math.round(file.size / 1024);
        const sizeStr = sizeKB > 1024 ? `${(sizeKB / 1024).toFixed(1)} MB` : `${sizeKB} KB`;
        addMedia({
          id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          name: file.name,
          url,
          size: sizeStr,
          uploadedAt: new Date().toISOString()
        });
      };
      reader.readAsDataURL(file);
    });
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };
  const handleSaveApply = async () => {
    try {
      setIsSaving(true);
      await saveTheme(customTheme);
      toast.success('Theme saved and applied successfully!');
    } catch (error) {
      toast.error('Failed to save theme.');
    } finally {
      setIsSaving(false);
    }
  };
  const handleSaveCustom = async () => {
    if (!customThemeName.trim()) return;
    try {
      await saveCustomTheme(customThemeName);
      toast.success('Custom theme saved!');
      setShowSaveModal(false);
      setCustomThemeName('');
    } catch (error) {
      toast.error('Failed to save custom theme.');
    }
  };
  const sections = [{
    id: 'prebuilt' as const,
    label: 'Prebuilt Themes',
    icon: Sparkles
  }, {
    id: 'colors' as const,
    label: 'Colors',
    icon: Palette
  }, {
    id: 'images' as const,
    label: 'Images & Uploads',
    icon: ImageIcon
  }, {
    id: 'typography' as const,
    label: 'Typography',
    icon: Type
  }, {
    id: 'components' as const,
    label: 'Components',
    icon: Square
  }];
  const fontOptions = ['Inter', 'Outfit', 'Poppins', 'Space Grotesk', 'DM Sans', 'Plus Jakarta Sans', 'Manrope', 'Sora'];
  return <div className="space-y-8 pb-32" data-id="element-828">
      <Toaster theme="dark" position="top-right" data-id="element-829" />
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-830">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-831">
          <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-832">
            Theme Editor
          </h1>
          <p className="text-slate-400" data-id="element-833">
            Customize the look and feel of your entire site.
          </p>
        </motion.div>
        <div className="flex gap-3" data-id="element-834">
          <button onClick={() => setShowPreview(!showPreview)} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-835">
            <Eye size={16} data-id="element-836" /> Preview
          </button>
          <button onClick={handleReset} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-837">
            <RotateCcw size={16} data-id="element-838" /> Reset
          </button>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-slate-800 pb-4 overflow-x-auto" data-id="element-839">
        {sections.map(s => <button key={s.id} onClick={() => setActiveSection(s.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeSection === s.id ? 'bg-fox-600 text-white' : 'bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-white'}`} data-id="element-840">
            <s.icon size={16} data-id="element-841" /> {s.label}
          </button>)}
      </div>

      {/* Live Preview Bar */}
      <AnimatePresence data-id="element-842">
        {showPreview && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="overflow-hidden" data-id="element-843">
            <div className="rounded-2xl border overflow-hidden" style={{
          borderColor: customTheme.colors.border
        }} data-id="element-844">
              {/* Mini Navbar Preview */}
              <div className="px-6 py-3 flex items-center justify-between" style={{
            backgroundColor: customTheme.colors.bgSecondary,
            borderBottom: `1px solid ${customTheme.colors.border}`
          }} data-id="element-845">
                <span style={{
              color: customTheme.colors.textPrimary,
              fontFamily: customTheme.fonts.heading,
              fontWeight: 700
            }} data-id="element-846">
                  Fox Line{' '}
                  <span style={{
                color: customTheme.colors.primary
              }} data-id="element-847">
                    Hosting
                  </span>
                </span>
                <div className="flex items-center gap-4" data-id="element-848">
                  <span style={{
                color: customTheme.colors.textSecondary,
                fontSize: '0.875rem'
              }} data-id="element-849">
                    Features
                  </span>
                  <span style={{
                color: customTheme.colors.textSecondary,
                fontSize: '0.875rem'
              }} data-id="element-850">
                    Pricing
                  </span>
                  <button style={{
                backgroundColor: customTheme.colors.primary,
                color: '#fff',
                padding: '0.375rem 1rem',
                borderRadius: customTheme.buttonStyle === 'pill' ? '9999px' : customTheme.buttonStyle === 'square' ? '0.25rem' : '0.5rem',
                fontSize: '0.75rem',
                fontWeight: 600
              }} data-id="element-851">
                    Login
                  </button>
                </div>
              </div>

              {/* Mini Hero Preview */}
              <div className="px-6 py-10 text-center" style={{
            backgroundColor: customTheme.colors.bgPrimary
          }} data-id="element-852">
                <h2 style={{
              color: customTheme.colors.textPrimary,
              fontFamily: customTheme.fonts.heading,
              fontSize: '1.5rem',
              fontWeight: 700,
              marginBottom: '0.5rem'
            }} data-id="element-853">
                  Hosting That Runs{' '}
                  <span style={{
                color: customTheme.colors.primary
              }} data-id="element-854">
                    Like a Fox
                  </span>
                </h2>
                <p style={{
              color: customTheme.colors.textMuted,
              fontSize: '0.875rem',
              marginBottom: '1rem'
            }} data-id="element-855">
                  Lightning-fast, ultra-reliable web hosting.
                </p>
                <button style={{
              backgroundColor: customTheme.colors.primary,
              color: '#fff',
              padding: '0.5rem 1.5rem',
              borderRadius: customTheme.buttonStyle === 'pill' ? '9999px' : customTheme.buttonStyle === 'square' ? '0.25rem' : '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              boxShadow: `0 0 20px ${customTheme.colors.primaryGlow}`
            }} data-id="element-856">
                  Get Started
                </button>
              </div>

              {/* Mini Cards Preview */}
              <div className="px-6 py-6 grid grid-cols-3 gap-3" style={{
            backgroundColor: customTheme.colors.bgSecondary
          }} data-id="element-857">
                {['Web Hosting', 'VPS Servers', 'SSL Certs'].map((name, i) => <div key={i} className="p-4 text-center" style={{
              backgroundColor: customTheme.cardStyle === 'glass' ? `${customTheme.colors.bgSurface}99` : customTheme.colors.bgCard,
              border: `1px solid ${customTheme.colors.border}`,
              borderRadius: customTheme.borderRadius,
              boxShadow: customTheme.cardStyle === 'raised' ? '0 4px 20px rgba(0,0,0,0.3)' : 'none',
              backdropFilter: customTheme.cardStyle === 'glass' ? 'blur(10px)' : 'none'
            }} data-id="element-858">
                    <div className="w-8 h-8 rounded-lg mx-auto mb-2 flex items-center justify-center" style={{
                backgroundColor: `${customTheme.colors.primary}20`
              }} data-id="element-859">
                      <div className="w-4 h-4 rounded" style={{
                  backgroundColor: customTheme.colors.primary
                }} data-id="element-860" />
                    </div>
                    <p style={{
                color: customTheme.colors.textPrimary,
                fontSize: '0.75rem',
                fontWeight: 600
              }} data-id="element-861">
                      {name}
                    </p>
                  </div>)}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Prebuilt Themes */}
      {activeSection === 'prebuilt' && <div className="space-y-8" data-id="element-862">
          {savedCustomThemes.length > 0 && <div data-id="element-863">
              <h3 className="text-xl font-bold text-white mb-4" data-id="element-864">
                My Saved Themes
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-865">
                {savedCustomThemes.map(theme => <motion.div key={theme.id} whileHover={{
            y: -4
          }} onClick={() => applyPrebuilt(theme)} className={`group cursor-pointer rounded-2xl border overflow-hidden transition-all relative ${currentTheme.id === theme.id ? 'border-fox-500 ring-2 ring-fox-500/20' : 'border-slate-800 hover:border-slate-700'}`} data-id="element-866">
                    <div className="h-28 relative" style={{
              backgroundColor: theme.colors.bgPrimary
            }} data-id="element-867">
                      <div className="absolute inset-x-0 top-0 h-8 flex items-center px-4" style={{
                backgroundColor: theme.colors.bgSecondary
              }} data-id="element-868">
                        <div className="w-2 h-2 rounded-full" style={{
                  backgroundColor: theme.colors.primary
                }} data-id="element-869" />
                        <div className="ml-2 w-12 h-1.5 rounded" style={{
                  backgroundColor: theme.colors.textMuted
                }} data-id="element-870" />
                      </div>
                      <div className="absolute bottom-4 left-4 right-4 flex gap-2" data-id="element-871">
                        {[theme.colors.primary, theme.colors.accent, theme.colors.bgSurface, theme.colors.textPrimary].map((c, i) => <div key={i} className="w-6 h-6 rounded-full border border-white/20" style={{
                  backgroundColor: c
                }} data-id="element-872" />)}
                      </div>
                      {currentTheme.id === theme.id && <div className="absolute top-2 right-2 w-6 h-6 bg-fox-500 rounded-full flex items-center justify-center" data-id="element-873">
                          <Check size={14} className="text-white" data-id="element-874" />
                        </div>}
                    </div>
                    <div className="p-4 flex items-center justify-between" style={{
              backgroundColor: theme.colors.bgSecondary
            }} data-id="element-875">
                      <div data-id="element-876">
                        <h3 className="text-white font-bold" data-id="element-877">{theme.name}</h3>
                        <p className="text-slate-400 text-xs mt-1" data-id="element-878">
                          {theme.navStyle} nav • {theme.cardStyle} cards
                        </p>
                      </div>
                      <button onClick={e => {
                e.stopPropagation();
                deleteCustomTheme(theme.id);
              }} className="w-8 h-8 rounded-full bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all" data-id="element-879">
                        <Trash2 size={14} data-id="element-880" />
                      </button>
                    </div>
                  </motion.div>)}
              </div>
            </div>}

          <div data-id="element-881">
            <h3 className="text-xl font-bold text-white mb-4" data-id="element-882">
              Prebuilt Themes
            </h3>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-883">
              {prebuiltThemes.map(theme => <motion.div key={theme.id} whileHover={{
            y: -4
          }} onClick={() => applyPrebuilt(theme)} className={`cursor-pointer rounded-2xl border overflow-hidden transition-all ${currentTheme.id === theme.id ? 'border-fox-500 ring-2 ring-fox-500/20' : 'border-slate-800 hover:border-slate-700'}`} data-id="element-884">
                  {/* Theme Preview Swatch */}
                  <div className="h-28 relative" style={{
              backgroundColor: theme.colors.bgPrimary
            }} data-id="element-885">
                    <div className="absolute inset-x-0 top-0 h-8 flex items-center px-4" style={{
                backgroundColor: theme.colors.bgSecondary
              }} data-id="element-886">
                      <div className="w-2 h-2 rounded-full" style={{
                  backgroundColor: theme.colors.primary
                }} data-id="element-887" />
                      <div className="ml-2 w-12 h-1.5 rounded" style={{
                  backgroundColor: theme.colors.textMuted
                }} data-id="element-888" />
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 flex gap-2" data-id="element-889">
                      {[theme.colors.primary, theme.colors.accent, theme.colors.bgSurface, theme.colors.textPrimary].map((c, i) => <div key={i} className="w-6 h-6 rounded-full border border-white/20" style={{
                  backgroundColor: c
                }} data-id="element-890" />)}
                    </div>
                    {currentTheme.id === theme.id && <div className="absolute top-2 right-2 w-6 h-6 bg-fox-500 rounded-full flex items-center justify-center" data-id="element-891">
                        <Check size={14} className="text-white" data-id="element-892" />
                      </div>}
                  </div>
                  <div className="p-4" style={{
              backgroundColor: theme.colors.bgSecondary
            }} data-id="element-893">
                    <h3 className="text-white font-bold" data-id="element-894">{theme.name}</h3>
                    <p className="text-slate-400 text-xs mt-1" data-id="element-895">
                      {theme.navStyle} nav • {theme.cardStyle} cards •{' '}
                      {theme.buttonStyle} buttons
                    </p>
                  </div>
                </motion.div>)}
            </motion.div>
          </div>
        </div>}

      {/* Color Editor */}
      {activeSection === 'colors' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-896">
          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-5" data-id="element-897">
            <h3 className="text-lg font-bold text-white mb-4" data-id="element-898">Brand Colors</h3>
            <ColorPicker label="Primary" value={customTheme.colors.primary} onChange={v => updateCustomColor('primary', v)} data-id="element-899" />
            <ColorPicker label="Primary Hover" value={customTheme.colors.primaryHover} onChange={v => updateCustomColor('primaryHover', v)} data-id="element-900" />
            <ColorPicker label="Accent" value={customTheme.colors.accent} onChange={v => updateCustomColor('accent', v)} data-id="element-901" />
            <ColorPicker label="Accent Hover" value={customTheme.colors.accentHover} onChange={v => updateCustomColor('accentHover', v)} data-id="element-902" />
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-5" data-id="element-903">
            <h3 className="text-lg font-bold text-white mb-4" data-id="element-904">
              Background Colors
            </h3>
            <ColorPicker label="Primary BG" value={customTheme.colors.bgPrimary} onChange={v => updateCustomColor('bgPrimary', v)} data-id="element-905" />
            <ColorPicker label="Secondary BG" value={customTheme.colors.bgSecondary} onChange={v => updateCustomColor('bgSecondary', v)} data-id="element-906" />
            <ColorPicker label="Surface" value={customTheme.colors.bgSurface} onChange={v => updateCustomColor('bgSurface', v)} data-id="element-907" />
            <ColorPicker label="Card BG" value={customTheme.colors.bgCard} onChange={v => updateCustomColor('bgCard', v)} data-id="element-908" />
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-5" data-id="element-909">
            <h3 className="text-lg font-bold text-white mb-4" data-id="element-910">Text Colors</h3>
            <ColorPicker label="Primary Text" value={customTheme.colors.textPrimary} onChange={v => updateCustomColor('textPrimary', v)} data-id="element-911" />
            <ColorPicker label="Secondary Text" value={customTheme.colors.textSecondary} onChange={v => updateCustomColor('textSecondary', v)} data-id="element-912" />
            <ColorPicker label="Muted Text" value={customTheme.colors.textMuted} onChange={v => updateCustomColor('textMuted', v)} data-id="element-913" />
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-5" data-id="element-914">
            <h3 className="text-lg font-bold text-white mb-4" data-id="element-915">Border Colors</h3>
            <ColorPicker label="Border" value={customTheme.colors.border} onChange={v => updateCustomColor('border', v)} data-id="element-916" />
            <ColorPicker label="Border Hover" value={customTheme.colors.borderHover} onChange={v => updateCustomColor('borderHover', v)} data-id="element-917" />
            <ColorPicker label="Primary Glow" value={customTheme.colors.primaryGlow.startsWith('rgba') ? '#ea580c' : customTheme.colors.primaryGlow} onChange={v => {
          const r = parseInt(v.slice(1, 3), 16);
          const g = parseInt(v.slice(3, 5), 16);
          const b = parseInt(v.slice(5, 7), 16);
          updateCustomColor('primaryGlow', `rgba(${r},${g},${b},0.3)`);
        }} data-id="element-918" />
          </div>
        </motion.div>}

      {/* Typography */}
      {activeSection === 'typography' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-919">
          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-920">
            <h3 className="text-lg font-bold text-white mb-6" data-id="element-921">Heading Font</h3>
            <div className="grid grid-cols-2 gap-3" data-id="element-922">
              {fontOptions.map(font => <button key={font} onClick={() => updateCustomFont('heading', font)} className={`p-4 rounded-xl border text-left transition-all ${customTheme.fonts.heading === font ? 'border-fox-500 bg-fox-500/10' : 'border-slate-800 hover:border-slate-700 bg-slate-900'}`} data-id="element-923">
                  <span className="text-white font-bold text-lg" style={{
              fontFamily: font
            }} data-id="element-924">
                    {font}
                  </span>
                  <p className="text-slate-500 text-xs mt-1" style={{
              fontFamily: font
            }} data-id="element-925">
                    The quick brown fox
                  </p>
                </button>)}
            </div>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-926">
            <h3 className="text-lg font-bold text-white mb-6" data-id="element-927">Body Font</h3>
            <div className="grid grid-cols-2 gap-3" data-id="element-928">
              {fontOptions.map(font => <button key={font} onClick={() => updateCustomFont('body', font)} className={`p-4 rounded-xl border text-left transition-all ${customTheme.fonts.body === font ? 'border-fox-500 bg-fox-500/10' : 'border-slate-800 hover:border-slate-700 bg-slate-900'}`} data-id="element-929">
                  <span className="text-white font-medium" style={{
              fontFamily: font
            }} data-id="element-930">
                    {font}
                  </span>
                  <p className="text-slate-500 text-xs mt-1" style={{
              fontFamily: font
            }} data-id="element-931">
                    The quick brown fox jumps over the lazy dog.
                  </p>
                </button>)}
            </div>
          </div>
        </motion.div>}

      {/* Component Styles */}
      {activeSection === 'components' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-932">
          {/* Navbar Style */}
          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-933">
            <h3 className="text-lg font-bold text-white mb-6" data-id="element-934">Navbar Style</h3>
            <div className="space-y-3" data-id="element-935">
              {(['transparent', 'solid', 'glass'] as const).map(style => <button key={style} onClick={() => {
            const u = {
              ...customTheme,
              navStyle: style
            };
            setCustomTheme(u);
            setTheme(u);
          }} className={`w-full p-4 rounded-xl border text-left transition-all flex items-center gap-3 ${customTheme.navStyle === style ? 'border-fox-500 bg-fox-500/10' : 'border-slate-800 hover:border-slate-700 bg-slate-900'}`} data-id="element-936">
                  <div className={`w-10 h-6 rounded ${style === 'transparent' ? 'bg-transparent border border-dashed border-slate-600' : style === 'solid' ? 'bg-slate-700' : 'bg-slate-700/50 backdrop-blur'}`} data-id="element-937" />
                  <div data-id="element-938">
                    <span className="text-white font-medium capitalize" data-id="element-939">
                      {style}
                    </span>
                    <p className="text-slate-500 text-xs" data-id="element-940">
                      {style === 'transparent' ? 'Blends with hero' : style === 'solid' ? 'Solid background' : 'Frosted glass effect'}
                    </p>
                  </div>
                </button>)}
            </div>
          </div>

          {/* Card Style */}
          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-941">
            <h3 className="text-lg font-bold text-white mb-6" data-id="element-942">Card Style</h3>
            <div className="space-y-3" data-id="element-943">
              {(['flat', 'raised', 'glass', 'bordered'] as const).map(style => <button key={style} onClick={() => {
            const u = {
              ...customTheme,
              cardStyle: style
            };
            setCustomTheme(u);
            setTheme(u);
          }} className={`w-full p-4 rounded-xl border text-left transition-all flex items-center gap-3 ${customTheme.cardStyle === style ? 'border-fox-500 bg-fox-500/10' : 'border-slate-800 hover:border-slate-700 bg-slate-900'}`} data-id="element-944">
                    <div className={`w-10 h-8 rounded-lg ${style === 'flat' ? 'bg-slate-800' : style === 'raised' ? 'bg-slate-800 shadow-lg shadow-black/30' : style === 'glass' ? 'bg-slate-800/50 border border-slate-700' : 'bg-transparent border-2 border-slate-600'}`} data-id="element-945" />
                    <div data-id="element-946">
                      <span className="text-white font-medium capitalize" data-id="element-947">
                        {style}
                      </span>
                      <p className="text-slate-500 text-xs" data-id="element-948">
                        {style === 'flat' ? 'Clean, no shadow' : style === 'raised' ? 'Elevated with shadow' : style === 'glass' ? 'Translucent blur' : 'Outline only'}
                      </p>
                    </div>
                  </button>)}
            </div>
          </div>

          {/* Button & Radius */}
          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-949">
            <h3 className="text-lg font-bold text-white mb-6" data-id="element-950">Button Style</h3>
            <div className="space-y-3 mb-8" data-id="element-951">
              {(['rounded', 'pill', 'square'] as const).map(style => <button key={style} onClick={() => {
            const u = {
              ...customTheme,
              buttonStyle: style
            };
            setCustomTheme(u);
            setTheme(u);
          }} className={`w-full p-4 rounded-xl border text-left transition-all flex items-center gap-3 ${customTheme.buttonStyle === style ? 'border-fox-500 bg-fox-500/10' : 'border-slate-800 hover:border-slate-700 bg-slate-900'}`} data-id="element-952">
                  {style === 'rounded' ? <RectangleHorizontal size={20} className="text-slate-400" data-id="element-953" /> : style === 'pill' ? <Circle size={20} className="text-slate-400" data-id="element-954" /> : <Square size={20} className="text-slate-400" data-id="element-955" />}
                  <span className="text-white font-medium capitalize" data-id="element-956">
                    {style}
                  </span>
                </button>)}
            </div>

            <h3 className="text-lg font-bold text-white mb-4" data-id="element-957">Border Radius</h3>
            <input type="range" min="0" max="2" step="0.25" value={parseFloat(customTheme.borderRadius)} onChange={e => {
          const u = {
            ...customTheme,
            borderRadius: `${e.target.value}rem`
          };
          setCustomTheme(u);
          setTheme(u);
        }} className="w-full accent-fox-500" data-id="element-958" />
            <div className="flex justify-between text-xs text-slate-500 mt-1" data-id="element-959">
              <span data-id="element-960">Sharp</span>
              <span data-id="element-961">{customTheme.borderRadius}</span>
              <span data-id="element-962">Round</span>
            </div>
          </div>
        </motion.div>}

      {/* Images & Uploads */}
      {activeSection === 'images' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="space-y-8" data-id="element-963">
          {/* Upload Area */}
          <div onDragOver={e => {
        e.preventDefault();
        setDragOver(true);
      }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} className={`border-2 border-dashed rounded-2xl p-10 text-center transition-colors ${dragOver ? 'border-fox-500 bg-fox-500/5' : 'border-slate-700 hover:border-slate-600 bg-slate-950'}`} data-id="element-964">
            <Upload size={40} className={`mx-auto mb-4 ${dragOver ? 'text-fox-500' : 'text-slate-600'}`} data-id="element-965" />
            <h3 className="text-white font-bold text-lg mb-2" data-id="element-966">Upload Images</h3>
            <p className="text-slate-400 text-sm mb-4" data-id="element-967">
              Drag & drop images here, or click to browse
            </p>
            <label className="inline-flex items-center gap-2 bg-fox-600 hover:bg-fox-500 text-white px-5 py-2.5 rounded-lg font-medium cursor-pointer transition-colors" data-id="element-968">
              <ImageIcon size={16} data-id="element-969" /> Choose Files
              <input type="file" accept="image/*" multiple className="hidden" onChange={e => handleFileUpload(e.target.files)} data-id="element-970" />
            </label>
            <p className="text-slate-600 text-xs mt-3" data-id="element-971">
              PNG, JPG, SVG, WebP up to 5MB each
            </p>
          </div>

          {/* Media Library */}
          <div data-id="element-972">
            <div className="flex items-center justify-between mb-4" data-id="element-973">
              <h3 className="text-lg font-bold text-white" data-id="element-974">
                Uploaded Media Library
              </h3>
              <span className="text-sm text-slate-500" data-id="element-975">
                {uploadedMedia.length} file
                {uploadedMedia.length !== 1 ? 's' : ''}
              </span>
            </div>

            {uploadedMedia.length === 0 ? <div className="bg-slate-950 border border-slate-800 rounded-2xl p-12 text-center" data-id="element-976">
                <ImageIcon size={48} className="mx-auto text-slate-700 mb-4" data-id="element-977" />
                <p className="text-slate-500" data-id="element-978">
                  No images uploaded yet. Upload images above to use them in
                  your theme.
                </p>
              </div> : <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-id="element-979">
                {uploadedMedia.map(media => <div key={media.id} className="group relative bg-slate-950 border border-slate-800 rounded-xl overflow-hidden hover:border-slate-700 transition-colors" data-id="element-980">
                    <div className="aspect-square bg-slate-900" data-id="element-981">
                      <img src={media.url} alt={media.name} className="w-full h-full object-cover" data-id="element-982" />
                    </div>
                    <div className="p-3" data-id="element-983">
                      <p className="text-white text-xs font-medium truncate" data-id="element-984">
                        {media.name}
                      </p>
                      <p className="text-slate-500 text-[10px]" data-id="element-985">{media.size}</p>
                    </div>
                    <button onClick={() => removeMedia(media.id)} className="absolute top-2 right-2 w-7 h-7 bg-red-500/80 hover:bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-986">
                      <Trash2 size={12} data-id="element-987" />
                    </button>
                  </div>)}
              </div>}
          </div>

          {/* Section Image Assignments */}
          <div data-id="element-988">
            <h3 className="text-lg font-bold text-white mb-4" data-id="element-989">
              Assign Images to Site Sections
            </h3>
            <p className="text-slate-400 text-sm mb-6" data-id="element-990">
              Select uploaded images or paste URLs to use as backgrounds for
              different sections of your site.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-991">
              {[{
            key: 'heroBg' as const,
            label: 'Hero Background',
            desc: 'Main landing hero section background image'
          }, {
            key: 'featuresBg' as const,
            label: 'Features Background',
            desc: 'Features section background image'
          }, {
            key: 'pricingBg' as const,
            label: 'Pricing Background',
            desc: 'Pricing section background image'
          }, {
            key: 'loginBg' as const,
            label: 'Login Background',
            desc: 'Login page background image'
          }, {
            key: 'logo' as const,
            label: 'Site Logo',
            desc: 'Replace the fox logo with a custom image'
          }].map(({
            key,
            label,
            desc
          }) => <div key={key} className="bg-slate-950 border border-slate-800 rounded-2xl p-5" data-id="element-992">
                  <div className="flex items-start justify-between mb-4" data-id="element-993">
                    <div data-id="element-994">
                      <h4 className="text-white font-bold" data-id="element-995">{label}</h4>
                      <p className="text-slate-500 text-xs mt-1" data-id="element-996">{desc}</p>
                    </div>
                    {customTheme.images[key] && <button onClick={() => updateImage(key, '')} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1" data-id="element-997">
                        <XIcon size={12} data-id="element-998" /> Remove
                      </button>}
                  </div>

                  {/* Current Image Preview */}
                  {customTheme.images[key] ? <div className="mb-4 rounded-lg overflow-hidden border border-slate-800 aspect-video bg-slate-900" data-id="element-999">
                      <img src={customTheme.images[key]} alt={label} className="w-full h-full object-cover" data-id="element-1000" />
                    </div> : <div className="mb-4 rounded-lg border border-dashed border-slate-700 aspect-video bg-slate-900 flex items-center justify-center" data-id="element-1001">
                      <span className="text-slate-600 text-sm" data-id="element-1002">
                        No image set
                      </span>
                    </div>}

                  {/* URL Input */}
                  <div className="space-y-3" data-id="element-1003">
                    <input type="text" value={customTheme.images[key]} onChange={e => updateImage(key, e.target.value)} placeholder="Paste image URL..." className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white focus:border-fox-500 focus:outline-none" data-id="element-1004" />

                    {/* Quick Select from Library */}
                    {uploadedMedia.length > 0 && <div data-id="element-1005">
                        <p className="text-xs text-slate-500 mb-2" data-id="element-1006">
                          Or select from library:
                        </p>
                        <div className="flex gap-2 overflow-x-auto pb-1" data-id="element-1007">
                          {uploadedMedia.map(media => <button key={media.id} onClick={() => updateImage(key, media.url)} className={`shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-colors ${customTheme.images[key] === media.url ? 'border-fox-500' : 'border-slate-700 hover:border-slate-600'}`} data-id="element-1008">
                              <img src={media.url} alt={media.name} className="w-full h-full object-cover" data-id="element-1009" />
                            </button>)}
                        </div>
                      </div>}
                  </div>
                </div>)}
            </div>
          </div>
        </motion.div>}

      {/* Sticky Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-slate-950/80 backdrop-blur-xl border-t border-slate-800 p-4 z-40" data-id="element-1010">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-1011">
          <div className="flex items-center gap-3" data-id="element-1012">
            {hasUnsavedChanges ? <>
                <div className="relative flex h-3 w-3" data-id="element-1013">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" data-id="element-1014"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500" data-id="element-1015"></span>
                </div>
                <span className="text-amber-500 text-sm font-medium" data-id="element-1016">
                  Unsaved changes
                </span>
              </> : <span className="text-slate-500 text-sm" data-id="element-1017">
                Theme is up to date
              </span>}
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-1018">
            <div className="relative" data-id="element-1019">
              <button onClick={() => setShowSaveModal(!showSaveModal)} className="w-full sm:w-auto px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-1020">
                Save As Custom Theme
              </button>

              {/* Save Modal Popover */}
              <AnimatePresence data-id="element-1021">
                {showSaveModal && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} className="absolute bottom-full right-0 mb-2 w-64 bg-slate-900 border border-slate-700 rounded-xl p-4 shadow-xl" data-id="element-1022">
                    <h4 className="text-white font-bold mb-2" data-id="element-1023">
                      Save Custom Theme
                    </h4>
                    <input type="text" value={customThemeName} onChange={e => setCustomThemeName(e.target.value)} placeholder="Theme Name" className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white focus:border-fox-500 focus:outline-none mb-3" autoFocus data-id="element-1024" />
                    <div className="flex gap-2" data-id="element-1025">
                      <button onClick={() => setShowSaveModal(false)} className="flex-1 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm transition-colors" data-id="element-1026">
                        Cancel
                      </button>
                      <button onClick={handleSaveCustom} disabled={!customThemeName.trim()} className="flex-1 px-3 py-1.5 bg-fox-600 hover:bg-fox-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm transition-colors" data-id="element-1027">
                        Save
                      </button>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
            <button onClick={handleSaveApply} disabled={!hasUnsavedChanges || isSaving} className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2 bg-fox-600 hover:bg-fox-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors" data-id="element-1028">
              <Save size={16} data-id="element-1029" />
              {isSaving ? 'Saving...' : 'Save & Apply'}
            </button>
          </div>
        </div>
      </div>
    </div>;
}